import type { Skill } from "./types";

/**
 * Catálogo de tabs. Mantenido CORTO para no inflar tokens upstream.
 * El LLM ve solo el label + purpose de una línea.
 */
const TABS: Record<string, { label: string; purpose: string }> = {
  info: { label: "Información", purpose: "Datos del restaurante" },
  preferencias: { label: "Preferencias", purpose: "Config carta digital + cocina" },
  mesas: { label: "Mesas", purpose: "CRUD de mesas físicas" },
  cocina: { label: "Cocina", purpose: "Pantalla operativa para chefs" },
  "dashboard-analitico": { label: "Dashboard", purpose: "Resumen analítico KPIs" },
  "rep-ventas": { label: "Reporte Ventas", purpose: "Analítica detallada de ventas" },
  "rep-productos": { label: "Reporte Productos", purpose: "Top productos analítico" },
  "rep-mesas": { label: "Reporte Mesas", purpose: "Mesas más usadas" },
  "rep-cocina": { label: "Reporte Cocina", purpose: "Throughput cocina" },
  "rep-caja": { label: "Reporte Caja", purpose: "Sesiones de caja analítico" },
  "rep-domicilios": { label: "Reporte Domicilios", purpose: "Métricas delivery" },
  "rep-stock": { label: "Reporte Stock", purpose: "Stock bajo + alertas" },
  "rep-personal": { label: "Reporte Personal", purpose: "Performance miembros" },
  "rep-reservas": { label: "Reporte Reservas", purpose: "Reservas analítico" },
  "rep-clientes": { label: "Reporte Clientes", purpose: "Top clientes" },
  reservas: { label: "Reservas", purpose: "Calendario operativo de reservas" },
  clientes: { label: "Clientes", purpose: "CRUD clientes" },
  "tomar-pedido": { label: "Tomar Pedido", purpose: "Iniciar pedido en mesa" },
  "tomar-domicilio": { label: "Tomar Domicilio", purpose: "Crear pedido delivery" },
  "corregir-pedido": { label: "Corregir Pedido", purpose: "Editar items de pedidos en curso" },
  entregas: { label: "Entregas", purpose: "Pedidos delivery listos para entregar" },
  "pedidos-completados": { label: "Órdenes Completadas", purpose: "Historial cerrados + factura" },
  alertas: { label: "Alertas", purpose: "Alertas pendientes" },
  productos: { label: "Productos", purpose: "CRUD catálogo + categorías" },
  adiciones: { label: "Adiciones", purpose: "Extras configurables" },
  "horario-semanal": { label: "Horario Semanal", purpose: "Días disponibles + descuentos" },
  carta: { label: "Carta", purpose: "Diseño carta pública" },
  caja: { label: "Cobrar Orden", purpose: "COBRAR mesas (NO abrir caja del día)" },
  "cobrar-domicilio": { label: "Cobrar Domicilio", purpose: "Cobrar pedidos delivery" },
  "sesiones-caja": { label: "Apertura/Cierre", purpose: "ABRIR Y CERRAR caja del día" },
  cajas: { label: "Cajas", purpose: "CRUD de cajas registradoras" },
  intermediarios: { label: "Intermediarios", purpose: "Bancos para transferencias" },
  "cuentas-credito": { label: "Cuentas de Crédito", purpose: "Cuentas a crédito clientes" },
  personal: { label: "Personal", purpose: "Miembros + roles" },
  invitaciones: { label: "Invitaciones", purpose: "Invitaciones pendientes" },
};

const VALID_TABS = Object.keys(TABS);

const TAB_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(TABS).map(([id, { label }]) => [id, label]),
);

const TAB_ENUM_DESCRIPTION = Object.entries(TABS)
  .map(([id, { label, purpose }]) => `${id}=${label}: ${purpose}`)
  .join(" | ");

export const navigateTabSkill: Skill = {
  id: "navigateTab",
  description: `Navega a una pestaña del dashboard. Disambiguación crítica:
- "abrir/cerrar caja del día" → "sesiones-caja" (NO "caja")
- "cobrar mesa" → "caja"
- "gestionar cajas registradoras" → "cajas"
- "ver pedidos en cocina (operativo)" → "cocina"; "estadísticas cocina" → "rep-cocina"
- "ver reservas (calendario)" → "reservas"; "estadísticas" → "rep-reservas"

Tabs: ${TAB_ENUM_DESCRIPTION}`,
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      tabId: { type: "string", enum: VALID_TABS },
    },
    required: ["restaurantId", "tabId"],
  },
  async run(args, ctx) {
    const restaurantId = String(args.restaurantId ?? "");
    const tabId = String(args.tabId ?? "");
    if (!restaurantId || !tabId) {
      return { ok: false, error: "Falta restaurantId o tabId." };
    }
    if (!VALID_TABS.includes(tabId)) {
      return { ok: false, error: `tabId inválido: '${tabId}'.` };
    }
    const label = TAB_LABELS[tabId] ?? tabId;
    const url = `/dashboard/restaurantes/${restaurantId}?tab=${encodeURIComponent(tabId)}`;

    if (ctx.follow.enabled && typeof window !== "undefined") {
      const isMobileViewport = window.innerWidth < 640;
      await ctx.follow.step(`Voy a abrir "${label}"`, { durationMs: 600 });

      if (isMobileViewport) {
        // ── Mobile: abrir el Sheet primero, luego clicar la tab adentro ──
        const desktopTab = document.querySelector(
          `[data-copilot-tab="${tabId}"]`,
        ) as HTMLElement | null;
        const visibleDesktopTab =
          desktopTab && desktopTab.offsetParent !== null ? desktopTab : null;

        if (!visibleDesktopTab) {
          const menuBtn = document.querySelector(
            "[data-copilot-mobile-menu]",
          ) as HTMLElement | null;
          if (menuBtn) {
            await ctx.follow.clickAt("[data-copilot-mobile-menu]", {
              durationMs: 700,
              label: "Menú",
            });
            menuBtn.click();
            // Esperar a que el Sheet renderice los items
            for (let i = 0; i < 20; i++) {
              await new Promise((r) => setTimeout(r, 60));
              if (
                document.querySelector(
                  `[data-copilot-mobile-tab="${tabId}"]`,
                )
              )
                break;
            }
          }
          const mobileTabSelector = `[data-copilot-mobile-tab="${tabId}"]`;
          const mobileTabBtn = document.querySelector(
            mobileTabSelector,
          ) as HTMLElement | null;
          if (mobileTabBtn) {
            await ctx.follow.clickAt(mobileTabSelector, {
              durationMs: 850,
              label: label,
            });
            mobileTabBtn.click();
            await ctx.follow.step(`✓ Abierto: ${label}`, {
              durationMs: 900,
              spinner: false,
            });
            ctx.follow.clear();
            return {
              ok: true,
              result: { navigatedTo: url },
              humanSummary: `Navegando a "${label}".`,
            };
          }
        }
      }

      // ── Desktop (o fallback): expandir grupo si está colapsado y clicar tab ──
      const tabBtn = document.querySelector(
        `[data-copilot-tab="${tabId}"]`,
      ) as HTMLElement | null;
      const groupName =
        tabBtn?.getAttribute("data-copilot-tab-group") || "";
      if (groupName) {
        const groupHeader = document.querySelector(
          `[data-copilot-group="${CSS.escape(groupName)}"]`,
        ) as HTMLElement | null;
        const isOpen =
          groupHeader?.getAttribute("data-copilot-group-open") === "true";
        if (groupHeader && !isOpen) {
          await ctx.follow.step(`Abriendo grupo "${groupName}"…`, {
            durationMs: 400,
          });
          await ctx.follow.clickAt(
            `[data-copilot-group="${CSS.escape(groupName)}"]`,
            { durationMs: 600, label: groupName },
          );
          groupHeader.click();
          await new Promise((r) => setTimeout(r, 320));
        }
      }

      await ctx.follow.clickAt(`[data-copilot-tab="${tabId}"]`, {
        durationMs: 850,
        label: label,
      });
      await new Promise((r) => setTimeout(r, 180));
    }

    ctx.router.push(url);

    if (ctx.follow.enabled) {
      await ctx.follow.step(`✓ Abierto: ${label}`, {
        durationMs: 900,
        spinner: false,
      });
      ctx.follow.clear();
    }

    return {
      ok: true,
      result: { navigatedTo: url },
      humanSummary: `Navegando a "${label}".`,
    };
  },
};
